import React, { useState, useRef, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { isOnlineSelector } from "data/reducers/selectors";
import { NewFormType, ItemFlowTemplateType } from "data/types";
import DocumentSelection from "./DocumentSelection";
import { type Props } from "./Factory";
import ItemFlows from "components/ItemFlows";
import { produce } from "immer";
import { getLocalizedText } from "data/utils";
import Divider from "components/Divider";
import CountBadge from "components/CountBadge";
import styles from "./itemFlow_document.module.css";
import isEmpty from "lodash/isEmpty";
import { docCreate } from "data/api";
import { isSuccessfullUpload } from "data/uppy";

const Document = (props: Props) => {
    const {
        defaultValues: defaultValuesFromProps,
        itemUri,
        onCreated,
        files: filesFromProps,
        viewName,
        flow,
    } = props;

    const isOnline = useSelector(isOnlineSelector);
    const [files, setFiles] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    // we'll keep a reference to the uppy instance created by <DocumentUpload>
    const uppyRef = useRef(null);

    const handleFilesUpdate = useCallback(() => {
        const files = uppyRef.current.getFiles();
        setFiles(files);
        /* istanbul ignore next */
        setUploadedFiles((fs) =>
            produce(fs, (draft) => {
                const index = draft.findIndex(
                    (f) => !files.some((df) => df.id === f.id)
                );
                if (index !== -1) draft.splice(index, 1);
            })
        );
    }, []);

    const handleFileUpload = useCallback(
        (file, response) =>
            setUploadedFiles((fs) =>
                produce(fs, (draft) => {
                    const uploadedFile = draft.find((f) => f.id === file.id);
                    const fileId = response.uploadURL.split("/").pop();
                    if (uploadedFile) {
                        uploadedFile.status = response.status;
                        uploadedFile.fileId = fileId;
                    } else {
                        draft.push({
                            id: file.id,
                            status: response.status,
                            fileId,
                        });
                    }
                })
            ),
        []
    );
    const currentFile = uploadedFiles?.[0];
    const webDavName =
        uppyRef.current?.getFiles()?.find((f) => f.id === currentFile?.id)?.meta
            ?.name ?? null;
    const defaultValues = useMemo(
        () =>
            produce(defaultValuesFromProps || {}, (draft) => {
                if (webDavName) draft["$NAME$"] = webDavName;
                else if (isEmpty(defaultValuesFromProps)) return null;
            }),
        [webDavName, defaultValuesFromProps]
    );

    const handleCreate = useCallback(
        async (
            model,
            keepOriginalFilename: boolean = false,
            formatId: number,
            applyToAll: boolean = false
        ) => {
            // if applyToAll is true, we upload all documents at once
            if (applyToAll && uploadedFiles.length > 0) {
                try {
                    const result = uploadedFiles.map((file) =>
                        docCreate({
                            itemUri,
                            values: model,
                            formatId,
                            keepOriginalFilename,
                            fileId: file.fileId,
                        })
                    );
                    const resolved = await Promise.all(result);
                    const success =
                        !isOnline ||
                        (resolved.every((file) => file) &&
                            isSuccessfullUpload(uploadedFiles));
                    return onCreated(success);
                } catch (error) {
                    return onCreated(false, error);
                }
            } else {
                // otherwise, we upload only the first document
                try {
                    const result = await docCreate({
                        itemUri,
                        values: model,
                        formatId,
                        keepOriginalFilename,
                        fileId: currentFile?.fileId,
                    });
                    const success =
                        !isOnline ||
                        (result && isSuccessfullUpload(uploadedFiles));
                    // if we have multiple files, we remove the one that was just uploaded, if we have only one file, we just call onCreated
                    return uploadedFiles?.length > 1
                        ? setUploadedFiles((fs) =>
                              produce(fs, (draft) =>
                                  draft.filter((f) => f.id !== currentFile?.id)
                              )
                          )
                        : onCreated(success);
                } catch (error) {
                    return onCreated(false, error);
                }
            }
        },
        [isOnline, itemUri, onCreated, uploadedFiles, currentFile]
    );

    return (
        <>
            <div
                className={styles.wrapper}
                style={{
                    flexGrow: files.length === 0 ? 1 : 0,
                }}
            >
                <Divider
                    className={styles.divider}
                    header={
                        <>
                            {getLocalizedText("$itemflows:toggles.file")}{" "}
                            {!!files.length && (
                                <CountBadge count={files.length} />
                            )}
                        </>
                    }
                    name="upload"
                    expanded={files.length === 0}
                    unmountOnExit={false}
                    mountOnEnter={false}
                >
                    <div>
                        <DocumentSelection
                            ref={uppyRef}
                            uppyId={viewName}
                            onFileAdded={handleFilesUpdate}
                            onFileRemoved={handleFilesUpdate}
                            onFileUpload={handleFileUpload}
                            openCustomUrl={flow.commandAction.props.url}
                            openBrowseDialog={
                                flow.commandAction.props.openBrowseDialog
                            }
                            openCamera={flow.commandAction.props.openCamera}
                            numOfFiles={files.length}
                            files={filesFromProps}
                        />
                    </div>
                </Divider>
            </div>
            {currentFile != null && (
                <ItemFlows.Form
                    {...props}
                    key={currentFile?.fileId}
                    formType={NewFormType.Document}
                    multiple={uploadedFiles.length > 1}
                    fileId={currentFile?.fileId}
                    templateType={ItemFlowTemplateType.Document}
                    templateId={flow.commandAction.props.templateId}
                    defaultValues={defaultValues}
                    onCreate={handleCreate}
                />
            )}
        </>
    );
};
Document.displayName = "ItemFlowsInput.Document";
export default Document;
